import { Fragment, useCallback, useMemo } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { FormatContent } from 'src/contexts/ContentContext';
import { Button } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getListingDetailsModalConfigWithDeepLink } from 'src/utils/inventoryUtils';
import { getSaleDetailsRelativeUrl } from 'src/utils/saleUtils';
import { Feature, ListingDetails, Ticket } from 'src/WebApiController';

import { LaunchViewAllSeats } from '../../actions/LaunchViewAllSeats';

const MAX_SEATS_DISPLAY = 10;

const getSeatDisplay = (ticket: Ticket, navigate?: NavigateFunction) => {
  const { saleId, seat, ord, spoiledSaleId } = ticket;

  const ticketSaleId = saleId ?? spoiledSaleId;
  if (ticketSaleId) {
    return (
      <Button
        key={ticket.id}
        variant={'link'}
        onClick={() =>
          navigate ? navigate(getSaleDetailsRelativeUrl(ticketSaleId!)) : null
        }
      >
        {seat ? (
          <>{seat}</>
        ) : (
          <FormatContent
            id={FormatContentId.SeatNumber}
            params={ord.toString()}
          />
        )}
      </Button>
    );
  } else {
    return seat ? (
      <>{seat}</>
    ) : (
      <FormatContent
        key={ticket.id}
        id={FormatContentId.SeatNumber}
        params={ord.toString()}
      />
    );
  }
};

export const useListingTickets = (listing?: ListingDetails) => {
  const isMobile = useMatchMedia('mobile');
  const navigate = useNavigate();
  const userHasWasteSaleFeature = useUserHasFeature(Feature.WasteSale);
  const userSpoilTicketsInSaleFeature = useUserHasFeature(
    Feature.SpoilTicketsInSale
  );

  const cancelTo = getListingDetailsModalConfigWithDeepLink(
    isMobile,
    listing?.id
  );
  const tickets = useMemo(() => listing?.tickets ?? [], [listing?.tickets]);

  const toSeatFragment = useCallback(
    (
      listing: ListingDetails,
      tickets: Ticket[],
      key: string,
      shouldNavigate?: boolean
    ): JSX.Element[] => {
      const ticketsElement = tickets
        .slice(0, MAX_SEATS_DISPLAY)
        .map((t) => (
          <Fragment key={t.id}>
            {getSeatDisplay(t, shouldNavigate ? navigate : undefined)}
          </Fragment>
        ))
        .flatMap((value, index, array) =>
          array.length - 1 !== index
            ? [value, <Fragment key={`comma_${index}`}>{', '}</Fragment>]
            : value
        );

      if (tickets.length > MAX_SEATS_DISPLAY) {
        ticketsElement.push(
          <Fragment key={key}>
            {' ... '}
            <LaunchViewAllSeats
              listingId={listing.id}
              marketplaceListingId={listing.idOnMkp}
              cancelTo={cancelTo}
              seeAvailableSeats
            />
          </Fragment>
        );
      }
      return ticketsElement;
    },
    [cancelTo, navigate]
  );

  const availableSeats = useMemo(() => {
    if (!tickets?.length || !listing) {
      return [];
    }
    const ticketsAvailable = tickets.filter(
      (t) => !t.saleId && !t.spoiledSaleId
    );
    return toSeatFragment(listing, ticketsAvailable, 'ViewAllSeats');
  }, [tickets, listing, userSpoilTicketsInSaleFeature, toSeatFragment]);

  const spoiledSeats = useMemo(() => {
    if (!tickets.length || !listing) {
      return [];
    }
    const ticketsSpoiled = tickets.filter((t) => !!t.spoiledSaleId);
    return toSeatFragment(listing, ticketsSpoiled, 'ViewAllSpoiledSeats', true);
  }, [listing, tickets, toSeatFragment]);

  const soldSeats = useMemo(() => {
    if (!tickets?.length || !listing) {
      return [];
    }
    const ticketsSold = tickets.filter((t) => t.saleId && !t.isWasted);
    return toSeatFragment(listing, ticketsSold, 'ViewAllSoldSeats', true);
  }, [tickets, listing, toSeatFragment]);

  const wastedTickets = useMemo(() => {
    if (!tickets?.length || !listing || !userHasWasteSaleFeature) {
      return [];
    }
    const wastedTickets = tickets.filter((t) => !!t.saleId && t.isWasted);

    return toSeatFragment(listing, wastedTickets, 'ViewAllWastedSeats', true);
  }, [listing, tickets, toSeatFragment, userHasWasteSaleFeature]);

  return useMemo(
    () => ({
      availableSeats,
      spoiledSeats,
      soldSeats,
      wastedTickets,
    }),
    [availableSeats, soldSeats, spoiledSeats, wastedTickets]
  );
};
